import {
	Action,
	type BillingCycle,
	DEFAULT_LIMIT,
	DEFAULT_PAGE,
	EnrollmentStatus,
	type PaginationRequest,
} from "@pilatestime/shared-types";
import {
	createFileRoute,
	Link,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	AlertTriangle,
	Calendar,
	CreditCard,
	Filter,
	Plus,
	Search,
	Users,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	type EnrollmentFilterParams,
	fetchOrganizationEnrollmentsQueryOptions,
} from "@/api/enrollments";
import type { PaginationMeta } from "@/components/data-table";
import { DataTable, useDataTable } from "@/components/data-table";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useEnrollmentExpiry } from "@/hooks/useEnrollmentExpiry";
import { calculateMonthlyAverage } from "@/utils/planPricingUtils";

// Default status filter: Active & Pending
const DEFAULT_STATUS_FILTER = [
	EnrollmentStatus.ACTIVE,
	EnrollmentStatus.PENDING,
];

// All available statuses for the filter
const ALL_STATUSES = Object.values(EnrollmentStatus);

// Search params schema
const enrollmentSearchSchema = z.object({
	page: z.number().min(1).catch(DEFAULT_PAGE),
	limit: z.number().min(1).max(100).catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
	expiringSoon: z.boolean().optional().catch(undefined),
	statuses: z.array(z.nativeEnum(EnrollmentStatus)).optional().catch(undefined),
});

interface EnrollmentListProps {
	organizationId: string;
	pagination: PaginationRequest;
	searchTerm?: string;
	statusFilters?: EnrollmentStatus[];
	expiringSoonFilter?: boolean;
	userId: string;
	token: string;
	canManageEnrollments: boolean;
	onPaginationMetaChange?: (meta: PaginationMeta) => void;
}

type EnrollmentRow = {
	id: string;
	customer: {
		id: string;
		firstName: string;
		lastName: string;
		email?: string;
		idType?: string;
		idNumber?: string;
	};
	customerPlan: {
		id: string;
		name: string;
		description: string;
		price: number;
		currency: string;
		billingCycle: string;
	};
	schedules: {
		id: string;
		name: string;
		description?: string;
		type: string;
		capacity?: number;
		startTime: string;
		endTime: string;
		startDate: number;
		endDate?: number;
		recurrencyType: string;
		daysOfWeek: string[];
	}[];
	status: EnrollmentStatus;
	startDate: number;
	endDate?: number;
	notes?: string;
	createdAt: number;
	updatedAt: number;
};

function EnrollmentList({
	organizationId,
	pagination,
	searchTerm,
	statusFilters,
	expiringSoonFilter,
	userId,
	token,
	canManageEnrollments,
	onPaginationMetaChange,
}: EnrollmentListProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { formatDate } = useDateFormatter();
	const { isCloseToExpire } = useEnrollmentExpiry();

	// Build filter params for the API
	const filterParams: EnrollmentFilterParams = useMemo(
		() => ({
			statuses: expiringSoonFilter ? undefined : statusFilters,
			expiringSoon: expiringSoonFilter || undefined,
		}),
		[statusFilters, expiringSoonFilter],
	);

	const {
		data: enrollmentsData,
		isLoading,
		error,
		paginationMeta,
	} = useDataTable<EnrollmentRow, EnrollmentFilterParams>({
		queryOptions: fetchOrganizationEnrollmentsQueryOptions,
		userId,
		token,
		organizationId,
		pagination,
		searchTerm,
		additionalParams: filterParams,
	});

	// Server-side filtering handles expiringSoon, so use data directly
	const enrollments = enrollmentsData;

	// Memoize pagination metadata to prevent unnecessary re-renders
	const memoizedPaginationMeta = useMemo(
		() => ({
			page: paginationMeta.page,
			limit: paginationMeta.limit,
			total: paginationMeta.total,
			totalPages: paginationMeta.totalPages,
			hasNextPage: paginationMeta.hasNextPage,
			hasPreviousPage: paginationMeta.hasPreviousPage,
		}),
		[
			paginationMeta.page,
			paginationMeta.limit,
			paginationMeta.total,
			paginationMeta.totalPages,
			paginationMeta.hasNextPage,
			paginationMeta.hasPreviousPage,
		],
	);

	// Pass pagination metadata up to parent
	useEffect(() => {
		if (onPaginationMetaChange) {
			onPaginationMetaChange(memoizedPaginationMeta);
		}
	}, [memoizedPaginationMeta, onPaginationMetaChange]);

	const handleEdit = useCallback(
		(enrollmentId: string) => {
			navigate({
				to: "/app/org/$orgId/enrollments/$enrollmentId/edit",
				params: { orgId: organizationId, enrollmentId },
			});
		},
		[navigate, organizationId],
	);

	const handleRowClick = useCallback(
		(enrollment: EnrollmentRow) => {
			handleEdit(enrollment.id);
		},
		[handleEdit],
	);

	const formatCurrency = useCallback((amount: number, currency: string) => {
		return new Intl.NumberFormat("pt-BR", {
			style: "currency",
			currency: currency,
		}).format(amount);
	}, []);

	const getStatusBadge = useCallback(
		(status: EnrollmentStatus) => {
			const statusClasses: Record<EnrollmentStatus, string> = {
				[EnrollmentStatus.PENDING]: "badge-warning",
				[EnrollmentStatus.ACTIVE]: "badge-success",
				[EnrollmentStatus.PAUSED]: "badge-warning",
				[EnrollmentStatus.CANCELED]: "badge-error",
				[EnrollmentStatus.COMPLETED]: "badge-info",
				[EnrollmentStatus.EXPIRED]: "badge-neutral",
			};

			return (
				<span className={`badge ${statusClasses[status]}`}>
					{t(`enrollments.status.${status.toLowerCase()}`)}
				</span>
			);
		},
		[t],
	);

	const columns: ColumnDef<EnrollmentRow>[] = useMemo(() => {
		const cols: ColumnDef<EnrollmentRow>[] = [
			{
				accessorKey: "customer",
				header: () => (
					<div className="flex items-center gap-2">
						<Users className="w-4 h-4" />
						<span>{t("enrollments.table.customer")}</span>
					</div>
				),
				cell: ({ row }) => {
					const customer = row.original.customer;
					const schedule = row.original.schedules[0];
					return (
						<div className="space-y-1">
							<div className="font-medium leading-tight truncate max-w-45 sm:max-w-none">
								{customer.firstName} {customer.lastName}
							</div>
							{customer.email && (
								<div className="text-sm text-base-content/70 hidden sm:block">
									{customer.email}
								</div>
							)}
							{customer.idNumber && (
								<div className="text-sm text-base-content/70 hidden sm:block">
									{customer.idType}: {customer.idNumber}
								</div>
							)}
							{/* Mobile: Show schedule info */}
							{schedule && (
								<div className="sm:hidden mt-1 text-sm text-base-content/70">
									{schedule.startTime} - {schedule.endTime}
								</div>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "customerPlan",
				header: () => (
					<div className="flex items-center gap-2">
						<CreditCard className="w-4 h-4" />
						<span>{t("enrollments.table.plan")}</span>
					</div>
				),
				meta: { hiddenOnMobile: true },
				cell: ({ row }) => {
					const plan = row.original.customerPlan;
					return (
						<div className="space-y-1">
							<span className="font-medium truncate max-w-50 sm:max-w-none block">
								{plan.name}
							</span>
							<div className="text-sm text-base-content/70">
								{formatCurrency(plan.price, plan.currency)} /{" "}
								{t(
									`customerPlans.billingCycle.${plan.billingCycle.toLowerCase()}`,
								)}
							</div>
							{plan.billingCycle !== "MONTHLY" && (
								<div className="text-xs text-base-content/50 mt-1">
									{(() => {
										const monthlyAverage = calculateMonthlyAverage(
											plan.price.toString(),
											plan.billingCycle as BillingCycle,
										);
										const formattedMonthlyAverage = new Intl.NumberFormat(
											"pt-BR",
											{
												style: "currency",
												currency: plan.currency,
											},
										).format(monthlyAverage);

										return t("enrollments.planHelper.totalForPeriod", {
											monthlyAmount: formattedMonthlyAverage,
										});
									})()}
								</div>
							)}
						</div>
					);
				},
			},
			{
				accessorKey: "schedules",
				header: () => (
					<div className="flex items-center gap-2">
						<Calendar className="w-4 h-4" />
						<span>{t("enrollments.table.schedule")}</span>
					</div>
				),
				cell: ({ row }) => {
					const schedules = row.original.schedules;
					if (schedules.length === 1) {
						const schedule = schedules[0];
						return (
							<div className="min-w-50">
								<div className="font-medium">{schedule.name}</div>
								<div className="text-sm text-base-content/70">
									{schedule.startTime} - {schedule.endTime}
								</div>
								<div className="text-sm text-base-content/70">
									{t(`schedules.type.${schedule.type.toLowerCase()}`)}
								</div>
								{schedule.daysOfWeek.length > 0 && (
									<div className="flex flex-wrap gap-1 mt-1">
										{schedule.daysOfWeek.map((day) => (
											<span key={day} className="badge badge-xs badge-ghost">
												{t(`common.${day.toLowerCase()}`).substring(0, 3)}
											</span>
										))}
									</div>
								)}
							</div>
						);
					}
					return (
						<div className="min-w-50">
							<div className="font-medium">
								{schedules.length}{" "}
								{t("enrollments.table.schedules").toLowerCase()}
							</div>
							<div className="flex flex-wrap gap-1 mt-1">
								{schedules.map((s) => (
									<span key={s.id} className="badge badge-sm badge-outline">
										{s.name}
									</span>
								))}
							</div>
						</div>
					);
				},
			},

			{
				accessorKey: "status",
				header: t("enrollments.table.status"),
				cell: ({ row }) => getStatusBadge(row.original.status),
			},
			{
				accessorKey: "startDate",
				header: t("enrollments.table.startDate"),
				meta: { hiddenOnMobile: true },
				cell: ({ row }) => formatDate(row.original.startDate),
			},
			{
				accessorKey: "endDate",
				header: t("enrollments.table.endDate"),
				meta: { hiddenOnMobile: true },
				cell: ({ row }) => {
					const endDate = row.original.endDate;
					const closeToExpire = isCloseToExpire(endDate);
					return (
						<div className="space-y-1">
							{closeToExpire && (
								<span className="badge badge-warning badge-xs gap-1 whitespace-nowrap">
									<AlertTriangle className="w-3 h-3" />
									{t("enrollments.badges.closeToExpire")}
								</span>
							)}
							<div>{endDate ? formatDate(endDate) : "-"}</div>
						</div>
					);
				},
			},
		];

		return cols;
	}, [t, formatCurrency, getStatusBadge, formatDate, isCloseToExpire]);

	return (
		<DataTable
			data={enrollments}
			isLoading={isLoading}
			error={error}
			columns={columns}
			testId="enrollments"
			canManage={canManageEnrollments}
			onRowClick={canManageEnrollments ? handleRowClick : undefined}
			emptyState={{
				title: t("enrollments.list.empty"),
				createButton: canManageEnrollments
					? {
							label: t("enrollments.actions.create"),
							href: `/app/org/${organizationId}/enrollments/new`,
						}
					: undefined,
			}}
		/>
	);
}

export const Route = createFileRoute("/_auth/app/_app/org/$orgId/enrollments/")(
	{
		beforeLoad: ({ context }) => {
			if (!context.userId) {
				throw new Error("Not authenticated");
			}
		},
		loader: async ({ context, params }) => {
			const { userId, token } = useApiParameters(
				context.userId,
				await context.get_token(),
			);
			return { userId, token, orgId: params.orgId };
		},
		validateSearch: enrollmentSearchSchema,
		component: EnrollmentsPage,
	},
);

function EnrollmentsPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/enrollments/",
	});
	const search = Route.useSearch();
	const { userId, token } = Route.useLoaderData();
	const navigate = useNavigate();

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Expiring soon filter from URL params
	const [expiringSoonFilter, setExpiringSoonFilter] = useState(
		search.expiringSoon ?? false,
	);

	// Status filter state - default to Active & Pending (now array of selected statuses)
	// Priority: URL statuses > expiringSoon filter (ACTIVE only) > default (ACTIVE & PENDING)
	const [selectedStatuses, setSelectedStatuses] = useState<EnrollmentStatus[]>(
		search.statuses ??
			(search.expiringSoon ? [EnrollmentStatus.ACTIVE] : DEFAULT_STATUS_FILTER),
	);

	// Compute actual status values based on filter selection
	const statusFilters = useMemo(() => {
		// If all statuses are selected, return undefined to show all
		if (selectedStatuses.length === ALL_STATUSES.length) {
			return undefined;
		}
		// If no status is selected, also show all
		if (selectedStatuses.length === 0) {
			return undefined;
		}
		return selectedStatuses;
	}, [selectedStatuses]);

	// Handle toggling individual status
	const handleToggleStatus = (status: EnrollmentStatus) => {
		setSelectedStatuses((prev) => {
			if (prev.includes(status)) {
				return prev.filter((s) => s !== status);
			}
			return [...prev, status];
		});
		setCurrentPage(1); // Reset to first page when filter changes
	};

	// Handle select all / deselect all
	const handleSelectAllStatuses = () => {
		if (selectedStatuses.length === ALL_STATUSES.length) {
			setSelectedStatuses([]);
		} else {
			setSelectedStatuses([...ALL_STATUSES]);
		}
		setCurrentPage(1);
	};

	// Reset to default filter
	const handleResetToDefault = () => {
		setSelectedStatuses(DEFAULT_STATUS_FILTER);
		setCurrentPage(1);
	};

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1); // Reset to first page when search term changes
		}, 300); // 300ms delay

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Access control
	const can = useCan();
	const canManageEnrollments = can(Action.MANAGE_ENROLLMENTS);

	// Pagination handlers
	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	};

	const handlePaginationMetaChange = useCallback(
		(meta: {
			page: number;
			limit: number;
			total: number;
			totalPages: number;
			hasNextPage: boolean;
			hasPreviousPage: boolean;
		}) => {
			setPaginationMeta(meta);
		},
		[],
	);

	const handleSearchChange = (newSearchTerm: string) => {
		setSearchTerm(newSearchTerm);
		// Page reset is handled by useEffect when debouncedSearchTerm changes
	};

	const displayedCount = useMemo(() => {
		if (!paginationMeta.total) {
			return 0;
		}

		const startIndex = (paginationMeta.page - 1) * paginationMeta.limit;
		const remaining = paginationMeta.total - startIndex;

		if (remaining <= 0) {
			return 0;
		}

		return Math.min(paginationMeta.limit, remaining);
	}, [paginationMeta.limit, paginationMeta.page, paginationMeta.total]);

	// Check if filters differ from default
	const isDefaultFilter =
		selectedStatuses.length === DEFAULT_STATUS_FILTER.length &&
		DEFAULT_STATUS_FILTER.every((s) => selectedStatuses.includes(s));

	const hasActiveFilters =
		!!searchTerm || !isDefaultFilter || expiringSoonFilter;

	const handleResetFilters = () => {
		handleSearchChange("");
		handleResetToDefault();
		setExpiringSoonFilter(false);
		// Clear URL param
		navigate({
			to: "/app/org/$orgId/enrollments",
			params: { orgId },
			search: { page: 1, limit: pageSize, search: "" },
		});
	};

	// Get filter summary text
	const getFilterSummaryText = () => {
		if (
			selectedStatuses.length === 0 ||
			selectedStatuses.length === ALL_STATUSES.length
		) {
			return t("enrollments.filters.allStatuses");
		}
		if (isDefaultFilter) {
			return t("enrollments.filters.activeAndPending");
		}
		return t("enrollments.filters.selectedStatuses", {
			count: selectedStatuses.length,
		});
	};

	return (
		<div className="h-full flex flex-col space-y-3">
			{/* Header */}
			<PageHeader
				title={t("enrollments.management.title")}
				description={t("enrollments.management.description")}
				showBackButton={false}
			/>
			{/* Enrollments Card */}
			<Card className="flex-1 flex flex-col overflow-hidden">
				<CardHeader className="pb-3 space-y-3">
					<div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
						{/* Search, filters and summary */}
						<div className="flex-1 w-full">
							<div className="space-y-2 rounded-lg border border-base-200 bg-base-50 p-3">
								<div className="flex flex-col sm:flex-row gap-3 w-full">
									{/* Search */}
									<div className="relative w-full sm:w-108">
										<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/40" />
										<Input
											type="search"
											placeholder={t(
												"enrollments.customerSelection.searchPlaceholder",
											)}
											value={searchTerm}
											onChange={(e) => handleSearchChange(e.target.value)}
											className="pl-10 pr-10"
											data-testid="enrollments-search-input"
											autoComplete="off"
											data-1p-ignore="true"
											data-lpignore="true"
											data-form-type="other"
										/>
										<div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
											{searchTerm && (
												<button
													type="button"
													onClick={() => handleSearchChange("")}
													className="text-base-content/40 hover:text-base-content/80 transition-colors"
													aria-label={t("common.clear")}
												>
													<X className="h-3 w-3" />
												</button>
											)}
											{searchTerm !== debouncedSearchTerm && (
												<div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary" />
											)}
										</div>
									</div>
									{/* Status Filter with Popover */}
									<Popover>
										<PopoverTrigger asChild>
											<Button
												variant="outline"
												className="w-full sm:w-auto gap-2"
												data-testid="enrollments-status-filter"
											>
												<Filter className="h-4 w-4" />
												{(expiringSoonFilter ||
													(!isDefaultFilter &&
														selectedStatuses.length > 0 &&
														selectedStatuses.length < ALL_STATUSES.length)) && (
													<span className="badge badge-primary badge-xs">
														{(expiringSoonFilter ? 1 : 0) +
															(!isDefaultFilter &&
															selectedStatuses.length > 0 &&
															selectedStatuses.length < ALL_STATUSES.length
																? selectedStatuses.length
																: 0)}
													</span>
												)}
											</Button>
										</PopoverTrigger>
										<PopoverContent className="w-64 p-0" align="start">
											<div className="p-3 space-y-3">
												{/* Expiring Soon Filter */}
												<div className="space-y-2">
													<button
														type="button"
														className="flex items-center space-x-2 px-1 py-1 hover:bg-base-200 rounded cursor-pointer w-full text-left"
														onClick={() => {
															setExpiringSoonFilter(!expiringSoonFilter);
															setCurrentPage(1);
														}}
													>
														<Checkbox
															id="expiring-soon-filter"
															checked={expiringSoonFilter}
															onCheckedChange={(checked) => {
																setExpiringSoonFilter(!!checked);
																setCurrentPage(1);
															}}
														/>
														<label
															htmlFor="expiring-soon-filter"
															className="text-sm cursor-pointer flex-1 flex items-center gap-2"
														>
															<AlertTriangle className="w-4 h-4 text-warning" />
															<span className="font-medium">
																{t("enrollments.filters.expiringSoon")}
															</span>
														</label>
													</button>
												</div>
												<div className="border-t border-base-200" />
												{/* Status Filter */}
												<div className="flex items-center justify-between">
													<span className="text-sm font-medium">
														{t("enrollments.filters.statusLabel")}
													</span>
													<div className="flex gap-1">
														<Button
															variant="ghost"
															size="sm"
															className="h-7 px-2 text-xs"
															onClick={handleSelectAllStatuses}
														>
															{selectedStatuses.length === ALL_STATUSES.length
																? t("common.none")
																: t("common.all")}
														</Button>
														<Button
															variant="ghost"
															size="sm"
															className="h-7 px-2 text-xs"
															onClick={handleResetToDefault}
														>
															{t("common.reset")}
														</Button>
													</div>
												</div>
												<div className="space-y-2">
													{ALL_STATUSES.map((status) => (
														<button
															type="button"
															key={status}
															className="flex items-center space-x-2 px-1 py-1 hover:bg-base-200 rounded cursor-pointer w-full text-left"
															onClick={() => handleToggleStatus(status)}
														>
															<Checkbox
																id={`status-${status}`}
																checked={selectedStatuses.includes(status)}
																onCheckedChange={() =>
																	handleToggleStatus(status)
																}
															/>
															<label
																htmlFor={`status-${status}`}
																className="text-sm cursor-pointer flex-1"
															>
																{t(
																	`enrollments.status.${status.toLowerCase()}`,
																)}
															</label>
														</button>
													))}
												</div>
											</div>
										</PopoverContent>
									</Popover>
								</div>
								<div className="flex flex-col gap-2 text-xs text-base-content/60 sm:flex-row sm:items-center sm:justify-between sm:text-sm">
									<span>
										{t("common.showingResults", {
											count: displayedCount,
											total: paginationMeta.total,
										})}
									</span>
									<div className="flex items-center gap-2">
										<span>{t("common.statusFiltersApplied")}:</span>
										<div className="flex flex-wrap items-center gap-2">
											{searchTerm && (
												<span
													className="badge badge-ghost badge-sm max-w-50 truncate"
													title={searchTerm}
												>
													{t("common.search")}:
													<span className="ml-1 font-medium">{searchTerm}</span>
												</span>
											)}
											{expiringSoonFilter && (
												<span className="badge badge-warning badge-sm gap-1">
													<AlertTriangle className="w-3 h-3" />
													{t("enrollments.filters.expiringSoon")}
												</span>
											)}
											<span className="badge badge-ghost badge-sm">
												{getFilterSummaryText()}
											</span>
											{hasActiveFilters && (
												<Button
													variant="ghost"
													size="sm"
													type="button"
													onClick={handleResetFilters}
													className="h-7 px-2 text-xs text-base-content/70 hover:text-base-content"
												>
													{t("common.clear")}
												</Button>
											)}
										</div>
									</div>
								</div>
							</div>
						</div>
						{/* Create new */}
						{canManageEnrollments && (
							<Link
								to="/app/org/$orgId/enrollments/new"
								params={{ orgId }}
								className="btn btn-primary btn-sm w-full sm:w-auto"
								data-testid="enrollments-add-button"
							>
								<Plus className="w-4 h-4 mr-2" />
								{t("enrollments.actions.create")}
							</Link>
						)}
					</div>
				</CardHeader>
				<CardContent className="flex-1 overflow-hidden flex flex-col space-y-4">
					{/* Table */}
					<div className="flex-1 overflow-hidden">
						<EnrollmentList
							organizationId={orgId}
							pagination={{
								page: currentPage,
								limit: pageSize,
							}}
							searchTerm={debouncedSearchTerm}
							statusFilters={statusFilters}
							expiringSoonFilter={expiringSoonFilter}
							userId={userId}
							token={token}
							canManageEnrollments={canManageEnrollments}
							onPaginationMetaChange={handlePaginationMetaChange}
						/>
					</div>
					{/* Pagination */}
					<div className="shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
